// 3_pages/empty_cart.js
// Empty cart (body.id--9 + empty): fetches bestsellers page HTML, extracts
// products and footer sections, caches them in localStorage, and injects a
// product block plus a reconstructed footer (empty cart has neither).

import { byShop, getShopKey } from "../0_global/shop_map";

export class EmptyCart {
	static config = {
		productCount: 12,
		productSelector: "#products > .product",
		selectors: {
			cs: {
				categoryTop: ".category-top",
				support: "#footer .custom-footer__banner92",
				heureka: "#footer .custom-footer__banner83",
				instagram: "#footer .custom-footer__instagram",
				newsletter: ["#footer .custom-footer__banner85", "#footer .custom-footer__banner84"],
				footerBottom: "#footer .footer-bottom",
			},
			sk: {
				categoryTop: ".category-top",
				support: "#footer .custom-footer__banner17",
				heureka: "#footer .custom-footer__banner19",
				instagram: "#footer .custom-footer__instagram",
				newsletter: ["#footer .custom-footer__banner21", "#footer .custom-footer__banner20"],
				footerBottom: "#footer .footer-bottom",
			},
			pl: {
				categoryTop: ".category-top",
				support: "#footer .custom-footer__banner42",
				heureka: "#footer .custom-footer__banner43",
				instagram: "#footer .custom-footer__instagram",
				newsletter: ["#footer .custom-footer__banner45", "#footer .custom-footer__banner44"],
				footerBottom: "#footer .footer-bottom",
			},
		},
		storageKey: "footer_empty_cart_05",
		storageHours: 24,
		heurekaBadge: {
			cs: { type: "11", shopId: "71751", name: "Natima", seoId: "natima-cz" },
			sk: { type: "11", shopId: "25070", name: "Natima", seoId: "natima-sk" },
		},
	};

	static productsClass = "empty-cart-products";
	static footerWrapClass = "empty-cart-footer";
	static footerBuiltClass = "empty-cart-built";

	start() {
		if (!document.body.classList.contains("id--9")) {
			this.observeLiveFooterAndCache();
			return;
		}

		document.addEventListener("ShoptetCartUpdated", () => this.onCartUpdated());
		document.addEventListener("ShoptetDOMCartContentLoaded", () => this.onCartUpdated());
		document.addEventListener("ShoptetDataLayerUpdated", () => this.onCartUpdated());
		if (this.isCartEmpty()) {
			this.loadAndRender();
			return;
		}
		this.observeLiveFooterAndCache();
	}

	onCartUpdated() {
		if (!this.isCartEmpty()) {
			this.hideExtras();
			return;
		}
		this.showExtras();
		if (!document.querySelector(`.${EmptyCart.productsClass}`)) {
			this.loadAndRender();
		}
	}

	isCartEmpty() {
		const shoptetData = window.dataLayer?.[0]?.shoptet;
		const cart = shoptetData?.cart;
		const cartItems = shoptetData?.cartInfo?.cartItems;
		if (Array.isArray(cart)) return cart.length === 0;
		if (Array.isArray(cartItems)) return cartItems.length === 0;
		return !!document.querySelector(".cart-inner.cart-empty") && !document.querySelector(".cart-table");
	}

	getSelectors() {
		return byShop(EmptyCart.config.selectors);
	}

	getSourceUrl() {
		return window.NAT.translations("emptyCartBestsellersUrl");
	}

	getHeurekaBadge() {
		return byShop(EmptyCart.config.heurekaBadge);
	}

	getCacheKey() {
		return `${EmptyCart.config.storageKey}_${getShopKey()}`;
	}

	getExpiryKey() {
		return `${this.getCacheKey()}_expiry`;
	}

	readCache() {
		try {
			const now = Date.now();
			const expiry = localStorage.getItem(this.getExpiryKey());
			const cached = localStorage.getItem(this.getCacheKey());
			if (!expiry || !cached || now >= parseInt(expiry, 10)) return null;
			const data = JSON.parse(cached);
			if (
				!data ||
				typeof data.categoryTopHtml !== "string" ||
				typeof data.productsHtml !== "string" ||
				typeof data.footerHtml !== "string" ||
				typeof data.footerBottomHtml !== "string"
			) {
				return null;
			}
			return data;
		} catch (error) {
			console.warn("[NAT] empty_cart.js: failed to read cache", error);
			return null;
		}
	}

	writeCache(data) {
		try {
			const ttl = EmptyCart.config.storageHours * 60 * 60 * 1000;
			localStorage.setItem(this.getCacheKey(), JSON.stringify(data));
			localStorage.setItem(this.getExpiryKey(), (Date.now() + ttl).toString());
		} catch (error) {
			console.warn("[NAT] empty_cart.js: failed to write cache", error);
		}
	}

	cacheHasCompleteHeureka(data) {
		const html = data?.footerHtml;
		if (!html || !html.includes("heureka-review")) return false;
		return /showHeurekaBadgeHere-11[\s\S]*?<img/i.test(html) || html.includes("im9.cz");
	}

	hasLiveHeurekaReviews() {
		return document.querySelectorAll("#footer #heureka-reviews-insert .heureka-review").length > 0;
	}

	hasLiveHeurekaBadge() {
		return !!document.querySelector("#footer #showHeurekaBadgeHere-11 img, #footer #showHeurekaBadgeHere-11 a");
	}

	isLiveFooterReady() {
		return this.hasLiveHeurekaReviews() && this.hasLiveHeurekaBadge();
	}

	observeLiveFooterAndCache() {
		const footer = document.querySelector("#footer");
		if (!footer) return;

		const save = () => {
			if (!this.isLiveFooterReady()) return;
			this.saveLiveFooterCache();
		};

		document.addEventListener("downloadAndSaveHeurekaReviews", () => {
			setTimeout(save, 0);
		});

		const heureka = footer.querySelector(this.getSelectors().heureka);
		if (heureka) {
			const observer = new MutationObserver(() => {
				if (!this.isLiveFooterReady()) return;
				save();
				observer.disconnect();
			});
			observer.observe(heureka, { childList: true, subtree: true });
		}

		window.addEventListener("load", save);
		save();
	}

	saveLiveFooterCache() {
		const existing = this.readCache() || {
			categoryTopHtml: "",
			productsHtml: "",
			footerHtml: "",
			footerBottomHtml: "",
		};
		existing.footerHtml = this.assembleFooterHtml(document);
		existing.footerBottomHtml = this.assembleFooterBottomHtml(document);
		if (!existing.footerHtml) return;
		this.writeCache(existing);
	}

	async loadAndRender() {
		if (this.loading) {
			this.needsRetry = true;
			return;
		}

		this.loading = true;
		this.needsRetry = false;
		try {
			await this.loadAndInject();
		} finally {
			this.loading = false;
		}

		if (this.needsRetry && this.isCartEmpty() && !document.querySelector(`.${EmptyCart.productsClass}`)) {
			this.loadAndRender();
		}
	}

	async loadAndInject() {
		const cached = this.readCache();
		if (cached?.productsHtml && this.cacheHasCompleteHeureka(cached)) {
			this.inject(cached);
			return;
		}

		try {
			const sourceUrl = this.getSourceUrl();
			const response = await fetch(sourceUrl);
			if (!response.ok) throw new Error(`Failed to fetch ${sourceUrl}`);
			const html = await response.text();
			const parser = new DOMParser();
			const doc = parser.parseFromString(html, "text/html");
			const assembled = this.assembleFromDocument(doc);
			if (!assembled.categoryTopHtml && !assembled.productsHtml && !assembled.footerHtml && !assembled.footerBottomHtml) return;
			const liveFooter = this.cacheHasCompleteHeureka(cached);
			const merged = {
				categoryTopHtml: assembled.categoryTopHtml || cached?.categoryTopHtml || "",
				productsHtml: assembled.productsHtml || cached?.productsHtml || "",
				footerHtml: liveFooter ? cached.footerHtml : cached?.footerHtml || "",
				footerBottomHtml: liveFooter ? cached.footerBottomHtml : assembled.footerBottomHtml || cached?.footerBottomHtml || "",
			};
			this.writeCache(merged);
			this.inject({
				...merged,
				footerHtml: merged.footerHtml || assembled.footerHtml,
				footerBottomHtml: merged.footerBottomHtml || assembled.footerBottomHtml,
			});
		} catch (error) {
			console.error("[NAT] empty_cart.js: fetch failed", error);
		}
	}

	assembleFromDocument(doc) {
		return {
			categoryTopHtml: this.assembleCategoryTopHtml(doc),
			productsHtml: this.assembleProductsHtml(doc),
			footerHtml: this.assembleFooterHtml(doc),
			footerBottomHtml: this.assembleFooterBottomHtml(doc),
		};
	}

	assembleCategoryTopHtml(doc) {
		const categoryTop = doc.querySelector(this.getSelectors().categoryTop);
		if (!categoryTop) return "";
		return this.cleanClone(categoryTop).outerHTML;
	}

	assembleProductsHtml(doc) {
		const products = doc.querySelectorAll(EmptyCart.config.productSelector);
		if (!products.length) return "";

		const block = document.createElement("div");
		block.className = "products-block";
		const limit = Math.min(EmptyCart.config.productCount, products.length);
		for (let i = 0; i < limit; i++) {
			block.appendChild(this.cleanClone(products[i]));
		}
		return block.outerHTML;
	}

	assembleFooterHtml(doc) {
		const customFooter = document.createElement("div");
		customFooter.className = "custom-footer";

		const selectors = this.getSelectors();
		["support", "heureka", "instagram"].forEach((key) => {
			const node = doc.querySelector(selectors[key]);
			if (!node) return;
			const clone = this.cleanClone(node);
			if (key === "heureka") this.fillHeurekaBadge(clone, node);
			customFooter.appendChild(clone);
		});

		selectors.newsletter.forEach((selector) => {
			const node = doc.querySelector(selector);
			if (node) customFooter.appendChild(this.cleanClone(node));
		});

		if (!customFooter.children.length) return "";
		return customFooter.outerHTML;
	}

	assembleFooterBottomHtml(doc) {
		const footerBottom = doc.querySelector(this.getSelectors().footerBottom);
		if (!footerBottom) return "";
		return this.cleanClone(footerBottom).outerHTML;
	}

	cleanClone(element) {
		const clone = element.cloneNode(true);
		clone.querySelectorAll("script").forEach((script) => script.remove());
		clone.querySelectorAll(".carousel-control").forEach((control) => control.remove());
		this.fixLazyImages(clone);
		return clone;
	}

	fixLazyImages(root) {
		root.querySelectorAll("img").forEach((img) => {
			const dataSrc = img.getAttribute("data-src");
			if (dataSrc && !img.getAttribute("src")) {
				img.setAttribute("src", dataSrc);
			}
		});
	}

	parseHeurekaWidget(source) {
		const scripts = [...source.querySelectorAll("script")].map((script) => script.textContent || "").join("\n");
		const match = scripts.match(/showWidget['"]\s*,\s*['"](\d+)['"]\s*,\s*['"](\d+)['"]\s*,\s*['"]([^'"]+)['"]\s*,\s*['"]([^'"]+)['"]/);
		if (!match) return null;
		return { type: match[1], shopId: match[2], name: match[3], seoId: match[4] };
	}

	fillHeurekaBadge(root, source) {
		const holder = root.querySelector('[id^="showHeurekaBadgeHere-"]');
		if (!holder || holder.querySelector("img")) return;
		const parsed = (source && this.parseHeurekaWidget(source)) || this.getHeurekaBadge();
		if (!parsed?.type || !parsed?.shopId || !parsed?.seoId) return;

		const imgBase = window.NAT.translations("emptyCartHeurekaBadgeImgBase");
		const reviewsBase = window.NAT.translations("emptyCartHeurekaReviewsUrl");
		const title = `${window.NAT.translations("emptyCartHeurekaBadgeTitle")}${parsed.name}`;

		const link = document.createElement("a");
		link.href = `${reviewsBase}${parsed.seoId}/recenze/`;
		link.target = "_blank";
		link.title = title;
		const img = document.createElement("img");
		img.src = `${imgBase}cb/${parsed.shopId}-${parsed.type}.png`;
		img.height = 130;
		img.width = 340;
		img.alt = title;
		img.loading = "eager";
		img.style.border = "0";
		link.appendChild(img);
		holder.appendChild(link);
	}

	inject(data) {
		if (data.categoryTopHtml || data.productsHtml) this.injectProducts(data);
		if (data.footerHtml) this.injectFooter(data.footerHtml);
		if (data.footerBottomHtml) this.injectFooterBottom(data.footerBottomHtml);
		this.hydrate();
	}

	injectProducts(data) {
		if (document.querySelector(`.${EmptyCart.productsClass}`)) return;

		const wrapper = document.createElement("div");
		wrapper.className = EmptyCart.productsClass;
		wrapper.innerHTML = `${data.categoryTopHtml || ""}${data.productsHtml || ""}`;

		const cartWrapper = document.querySelector("#cart-wrapper, .cart-wrapper");
		if (cartWrapper?.parentElement) {
			cartWrapper.parentElement.insertAdjacentElement("afterend", wrapper);
			return;
		}

		const content = document.querySelector("#content .content-inner") || document.querySelector("#content");
		if (content) content.insertAdjacentElement("afterend", wrapper);
	}

	injectFooter(footerHtml) {
		if (document.querySelector(`.${EmptyCart.footerWrapClass}`)) return;

		const wrap = document.createElement("div");
		wrap.className = EmptyCart.footerWrapClass;
		wrap.innerHTML = footerHtml;

		let footer = document.querySelector("#footer");
		if (!footer) {
			footer = document.createElement("footer");
			footer.id = "footer";
			footer.className = `footer ${EmptyCart.footerBuiltClass}`;
			const rows = document.createElement("div");
			rows.className = "container footer-rows";
			footer.appendChild(rows);
			this.placeNewFooter(footer);
		}

		const rows = footer.querySelector(".footer-rows") || footer;
		const footerBottom = footer.querySelector(".footer-bottom");
		if (footerBottom) {
			footerBottom.insertAdjacentElement("beforebegin", wrap);
		} else {
			rows.appendChild(wrap);
		}
	}

	injectFooterBottom(footerBottomHtml) {
		const footer = document.querySelector("#footer");
		if (!footer || footer.querySelector(".footer-bottom")) return;
		footer.insertAdjacentHTML("beforeend", footerBottomHtml);
	}

	placeNewFooter(footer) {
		const overall = document.querySelector(".overall-wrapper");
		if (overall) {
			overall.insertAdjacentElement("afterend", footer);
			return;
		}
		const content = document.querySelector("#content");
		if (content) {
			content.insertAdjacentElement("afterend", footer);
			return;
		}
		document.body.appendChild(footer);
	}

	hideExtras() {
		document.querySelectorAll(`.${EmptyCart.productsClass}, .${EmptyCart.footerWrapClass}`).forEach((el) => {
			el.hidden = true;
		});
		const builtFooter = document.querySelector(`#footer.${EmptyCart.footerBuiltClass}`);
		if (builtFooter) builtFooter.hidden = true;
	}

	showExtras() {
		document.querySelectorAll(`.${EmptyCart.productsClass}, .${EmptyCart.footerWrapClass}`).forEach((el) => {
			el.hidden = false;
		});
		const builtFooter = document.querySelector(`#footer.${EmptyCart.footerBuiltClass}`);
		if (builtFooter) builtFooter.hidden = false;
	}

	getShowAllText(variant) {
		if (variant === "mobile") return window.NAT.translations("showAll");
		return window.NAT.translations("emptyCartShowAllBestsellers");
	}

	injectShowAllLink() {
		const title = document.querySelector(`.${EmptyCart.productsClass} .category-title`);
		if (!title) return;
		if (title.parentElement?.classList.contains("empty-cart-category-heading")) return;

		const sourceUrl = this.getSourceUrl();
		if (!title.querySelector("a")) {
			const titleLink = document.createElement("a");
			titleLink.href = sourceUrl;
			while (title.firstChild) titleLink.appendChild(title.firstChild);
			title.appendChild(titleLink);
		}

		const heading = document.createElement("div");
		heading.className = "empty-cart-category-heading";
		title.parentNode.insertBefore(heading, title);
		heading.appendChild(title);

		const link = document.createElement("a");
		link.className = "empty-cart-show-all";
		link.href = sourceUrl;

		const desktopText = document.createElement("span");
		desktopText.className = "empty-cart-show-all__desktop";
		desktopText.textContent = this.getShowAllText("desktop");

		const mobileText = document.createElement("span");
		mobileText.className = "empty-cart-show-all__mobile";
		mobileText.textContent = this.getShowAllText("mobile");

		link.appendChild(desktopText);
		link.appendChild(mobileText);
		heading.appendChild(link);
	}

	hydrate() {
		this.injectShowAllLink();
		this.fillHeurekaBadge(document);
		this.hydrateProductSlider();
		this.hydrateHeurekaSlider();
		this.hydrateInstagramFollow();
		this.hydrateLanguageToggle();
		if (typeof shoptet !== "undefined") {
			shoptet.images?.unveil?.();
			shoptet.validator?.initNewValidator?.();
		}
		document.dispatchEvent(new Event("ShoptetDOMContentLoaded"));
		if (!document.querySelector("#footer .heureka-review")) {
			document.dispatchEvent(new CustomEvent("emptyCartFooterInjected"));
		}
		this.observeLiveFooterAndCache();
	}

	hydrateProductSlider() {
		if (!window.NAT?.utils?.inicializeSliderElement) return;
		const block = document.querySelector(`.${EmptyCart.productsClass} .products-block`);
		if (!block) return;
		const products = block.querySelectorAll(".product");
		if (!products.length) return;
		window.NAT.utils.inicializeSliderElement(null, block, products, "products-slider", "img");
	}

	hydrateHeurekaSlider() {
		if (!window.NAT?.utils?.inicializeSliderElement) return;
		const footer = document.querySelector("#footer");
		if (!footer) return;
		const heurekaWrapper = footer.querySelector(".heureka-reviews-wrapper");
		const heurekaParent = footer.querySelector("#heureka-reviews-insert");
		const heurekaReviews = footer.querySelectorAll(".heureka-review");
		if (!heurekaWrapper || !heurekaParent || !heurekaReviews.length) return;
		window.NAT.utils.inicializeSliderElement(heurekaWrapper, heurekaParent, heurekaReviews, "heureka-slider", null);
	}

	hydrateInstagramFollow() {
		const footer = document.querySelector("#footer");
		if (!footer) return;
		const followBtn = footer.querySelector(".instagram-follow-btn a");
		if (!followBtn) return;
		if (followBtn.querySelector(".instagram-profile-name")) return;

		const socialLink = footer.querySelector("a.footer-social-link.instagram");
		if (!socialLink) return;
		const profileName = socialLink.getAttribute("href")?.replace("https://www.instagram.com/", "").replace("/", "");
		if (!profileName) return;

		const nameEl = document.createElement("span");
		nameEl.className = "instagram-profile-name";
		nameEl.textContent = profileName;
		const placeholder = document.createElement("span");
		placeholder.className = "instagram-image-placeholder";
		followBtn.prepend(nameEl);
		followBtn.prepend(placeholder);
	}

	hydrateLanguageToggle() {
		if (!window.NAT?.utils?.addSmartTouchClickListener) return;
		const footer = document.querySelector("#footer");
		if (!footer) return;
		const languagesWrapper = footer.querySelector(".footer-languages-wrapper");
		if (!languagesWrapper || languagesWrapper.dataset.emptyCartBound) return;
		const toggleButton = languagesWrapper.querySelector(".footer-languages-current");
		if (!toggleButton) return;
		languagesWrapper.dataset.emptyCartBound = "true";
		window.NAT.utils.addSmartTouchClickListener(toggleButton, function () {
			languagesWrapper.classList.toggle("active");
		});
	}
}

export function init() {
	new EmptyCart().start();
}
